import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navigation from '../../../Layouts/Navigation/Navigation';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faCircleCheck,
  faTriangleExclamation,
  faMagnifyingGlass,
  faPenToSquare,
  faBuilding,
  faFilter,
} from '@fortawesome/free-solid-svg-icons';
import { BASE_URL } from '../../../utils/constants';

const ESTADO_LABELS = {
  al_dia: { label: 'Al día', color: 'bg-green-100 text-green-700', icon: faCircleCheck, iconColor: 'text-green-500' },
  en_mora: { label: 'En mora', color: 'bg-red-100 text-red-600', icon: faTriangleExclamation, iconColor: 'text-red-500' },
};

const ControlCartera = () => {
  const { id_torre } = useParams();
  const navigate = useNavigate();

  const [towerName, setTowerName] = useState('');
  const [apartments, setApartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterEstado, setFilterEstado] = useState('todos');

  // Modal de edición
  const [editModal, setEditModal] = useState(false);
  const [selectedApto, setSelectedApto] = useState(null);
  const [nuevoEstado, setNuevoEstado] = useState('');
  const [observacion, setObservacion] = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null); // { type: 'success'|'error', text }

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const loadData = useCallback(() => {
    setLoading(true);
    axios
      .get(`${BASE_URL}/cartera/torre/${id_torre}`, { headers })
      .then(res => {
        const data = res.data;
        if (data.length > 0) setTowerName(data[0].torre_nombre);
        setApartments(data);
      })
      .catch(err => console.error('Error cargando cartera:', err))
      .finally(() => setLoading(false));
  }, [id_torre]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadData(); }, [loadData]);

  const openEdit = (apto) => {
    setSelectedApto(apto);
    setNuevoEstado(apto.estado_cartera);
    setObservacion(apto.observacion_mora || '');
    setEditModal(true);
    setMsg(null);
  };

  const handleSave = async () => {
    if (!selectedApto) return;
    setSaving(true);
    try {
      await axios.put(
        `${BASE_URL}/cartera/${selectedApto.id_apartamento}`,
        { estado_cartera: nuevoEstado, observacion_mora: observacion },
        { headers }
      );
      // Actualizar estado local inmediatamente sin esperar recarga
      setApartments(prev => prev.map(a =>
        a.id_apartamento === selectedApto.id_apartamento
          ? { ...a, estado_cartera: nuevoEstado, observacion_mora: observacion, fecha_cambio_cartera: new Date().toISOString() }
          : a
      ));
      setEditModal(false);
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.message || 'Error al actualizar.' });
    } finally {
      setSaving(false);
    }
  };

  const filtered = apartments.filter(a => {
    const matchEstado = filterEstado === 'todos' || a.estado_cartera === filterEstado;
    const q = search.toLowerCase();
    const matchSearch =
      !q ||
      String(a.num_apartamento).toLowerCase().includes(q) ||
      String(a.piso).includes(q) ||
      (a.residentes && a.residentes.toLowerCase().includes(q));
    return matchEstado && matchSearch;
  });

  const stats = {
    al_dia: apartments.filter(a => a.estado_cartera === 'al_dia').length,
    en_mora: apartments.filter(a => a.estado_cartera === 'en_mora').length,
  };

  const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  return (
    <>
      <Navigation />
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 sm:p-6 lg:p-8">
        <div className="max-w-6xl mx-auto">

          {/* Header */}
          <div className="flex items-center gap-3 mb-6">
            <button
              onClick={() => navigate('/adminCartera')}
              className="p-2 rounded-xl hover:bg-gray-200 transition-colors"
            >
              <FontAwesomeIcon icon={faArrowLeft} className="text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                <FontAwesomeIcon icon={faBuilding} className="text-amber-500" />
                {towerName || 'Cartera'}
              </h1>
              <p className="text-sm text-gray-500">Estado de cartera por apartamento</p>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
            <div className="bg-white rounded-xl shadow p-4 text-center">
              <p className="text-2xl font-bold text-gray-800">{apartments.length}</p>
              <p className="text-xs text-gray-500 mt-1">Unidades</p>
            </div>
            <div className="bg-white rounded-xl shadow p-4 text-center">
              <p className="text-2xl font-bold text-green-600">{stats.al_dia}</p>
              <p className="text-xs text-gray-500 mt-1">Al día</p>
            </div>
            <div className="bg-white rounded-xl shadow p-4 text-center">
              <p className="text-2xl font-bold text-red-500">{stats.en_mora}</p>
              <p className="text-xs text-gray-500 mt-1">En mora</p>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-2xl shadow p-4 mb-6 flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <FontAwesomeIcon icon={faMagnifyingGlass} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar por apto, piso o residente..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="w-full pl-9 pr-3 py-2 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-amber-300 text-sm"
              />
            </div>
            <div className="relative">
              <FontAwesomeIcon icon={faFilter} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
              <select
                value={filterEstado}
                onChange={e => setFilterEstado(e.target.value)}
                className="pl-8 pr-4 py-2 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-amber-300 text-sm bg-white"
              >
                <option value="todos">Todos los estados</option>
                <option value="al_dia">Al día</option>
                <option value="en_mora">En mora</option>
              </select>
            </div>
          </div>

          {/* Table */}
          {loading ? (
            <div className="flex justify-center py-16">
              <div className="animate-spin rounded-full h-10 w-10 border-4 border-amber-500 border-t-transparent"></div>
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100 bg-gray-50">
                      <th className="text-left px-4 py-3 font-semibold text-gray-600">Piso</th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-600">Apto</th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-600">Residente(s)</th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-600">Estado</th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-600 hidden md:table-cell">Observación</th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-600 hidden lg:table-cell">Último cambio</th>
                      <th className="text-center px-4 py-3 font-semibold text-gray-600">Acción</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="text-center py-12 text-gray-400">
                          No se encontraron apartamentos.
                        </td>
                      </tr>
                    ) : (
                      filtered.map(apto => {
                        const cfg = ESTADO_LABELS[apto.estado_cartera] || ESTADO_LABELS.al_dia;
                        return (
                          <tr key={apto.id_apartamento} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                            <td className="px-4 py-3 text-gray-700 font-medium">{apto.piso}</td>
                            <td className="px-4 py-3 text-gray-800 font-semibold">{apto.num_apartamento}</td>
                            <td className="px-4 py-3 text-gray-600 max-w-[160px] truncate">
                              {apto.residentes || <span className="text-gray-300 italic">Sin residentes</span>}
                            </td>
                            <td className="px-4 py-3">
                              <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${cfg.color}`}>
                                <FontAwesomeIcon icon={cfg.icon} className={cfg.iconColor} />
                                {cfg.label}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-gray-500 max-w-[180px] truncate hidden md:table-cell">
                              {apto.observacion_mora || '—'}
                            </td>
                            <td className="px-4 py-3 text-gray-400 text-xs hidden lg:table-cell">
                              {formatDate(apto.fecha_cambio_cartera)}
                            </td>
                            <td className="px-4 py-3 text-center">
                              <button
                                onClick={() => openEdit(apto)}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-50 hover:bg-amber-100 text-amber-700 text-xs font-semibold transition-colors"
                              >
                                <FontAwesomeIcon icon={faPenToSquare} />
                                Editar
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal edición */}
      {editModal && selectedApto && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="p-6">
              <h2 className="text-lg font-bold text-gray-800 mb-1">
                Actualizar cartera — Apto {selectedApto.num_apartamento}
              </h2>
              <p className="text-sm text-gray-500 mb-5">
                {selectedApto.residentes || 'Sin residentes registrados'}
              </p>

              {/* Estado */}
              <label className="block text-sm font-semibold text-gray-700 mb-2">Estado de cartera</label>
              <div className="flex gap-3 mb-5">
                {['al_dia', 'en_mora'].map(estado => {
                  const cfg = ESTADO_LABELS[estado];
                  const selected = nuevoEstado === estado;
                  return (
                    <button
                      key={estado}
                      onClick={() => setNuevoEstado(estado)}
                      className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl border-2 text-sm font-semibold transition-all
                        ${selected
                          ? estado === 'al_dia'
                            ? 'border-green-500 bg-green-50 text-green-700'
                            : 'border-red-500 bg-red-50 text-red-600'
                          : 'border-gray-200 text-gray-400 hover:border-gray-300'
                        }`}
                    >
                      <FontAwesomeIcon icon={cfg.icon} />
                      {cfg.label}
                    </button>
                  );
                })}
              </div>

              {/* Observación */}
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Observación {nuevoEstado === 'en_mora' && <span className="text-red-400">(recomendado)</span>}
              </label>
              <textarea
                value={observacion}
                onChange={e => setObservacion(e.target.value)}
                placeholder={nuevoEstado === 'en_mora' ? 'Ej: Debe 2 meses de administración...' : 'Opcional'}
                rows={3}
                maxLength={500}
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300 resize-none"
              />
              <p className="text-xs text-gray-400 text-right mt-1">{observacion.length}/500</p>

              {/* Alerta restricciones */}
              {nuevoEstado === 'en_mora' && (
                <div className="mt-3 p-3 bg-red-50 border border-red-100 rounded-xl text-xs text-red-600">
                  <FontAwesomeIcon icon={faTriangleExclamation} className="mr-1.5" />
                  Al marcar en mora se bloquearán: <strong>visitas con parqueadero</strong> y <strong>recepción de paquetería</strong>.
                  Los residentes serán notificados.
                </div>
              )}

              {msg && (
                <div className={`mt-3 p-3 rounded-xl text-xs font-semibold ${msg.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600'}`}>
                  {msg.text}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 pb-6 flex gap-3">
              <button
                onClick={() => setEditModal(false)}
                className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-600 text-sm font-semibold hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold transition-colors disabled:opacity-60"
              >
                {saving ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ControlCartera;
